import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callApi, callAdminApi } from '../../utils/apiCall';

const initialState = {
    permissions: {},
    roles: [],
    loading: false,
    error: null,
    // practiceId the current `permissions` map was fetched for. Tracked so
    // `hasFeatureAccess` can detect a stale-practice read during the
    // practice-switch window (old practice's permissions are still in the
    // slice while `fetchMyPermissions(newId)` is in flight) and refuse to
    // green-light feature-gated thunks for the *new* practice based on the
    // *old* practice's answers. See utils/featurePermission.js.
    practiceId: null,
    // practiceId the most recent `fetchMyPermissions` targeted. Set when loading
    // flips to true AND retained when loading flips to false, so it doubles as a
    // "we have attempted/resolved a permission fetch for this practice" signal —
    // true on BOTH the 200 path and any error/429 path (the thunk's finally always
    // stamps it). NewLeadTable's permissionsReady gate keys off this so the table
    // still loads when the permissions fetch fails (the success-only `practiceId`
    // stamp would leave the gate deadlocked). Only `clearPermissions` resets it.
    // This is independent of the `permissions` map / `practiceId` snapshot, so it
    // does NOT change hasFeatureAccess semantics (no fail-open introduced).
    loadingPracticeId: null,
    lastFetchStatus: null,
    // Super Admin state
    features: [],
    practiceFeatures: [],
    rolePermissions: [],
    userOverrides: [],
};

const slice = createSlice({
    name: 'permissions',
    initialState,
    reducers: {
        setPermissions(state, action) {
            state.permissions = action.payload.permissions || {};
            state.roles = action.payload.roles || [];
            // payload.practiceId is supplied by fetchMyPermissions — record
            // which practice this snapshot is for so feature-gate callers
            // can validate against the current target.
            if (action.payload.practiceId !== undefined) {
                state.practiceId = action.payload.practiceId;
            }
        },
        setLastFetchStatus(state, action) {
            state.lastFetchStatus = action.payload;
        },
        setLoading(state, action) {
            // Accept either a boolean (legacy) or {loading, practiceId}.
            // loadingPracticeId is RETAINED when loading flips to false so it records
            // the last practice we attempted/resolved a fetch for (success OR failure).
            // Only the object form carrying a practiceId updates it; the legacy boolean
            // form (used by the super-admin thunks) leaves it untouched so it never
            // wipes a my-permissions resolution. clearPermissions resets it on logout.
            if (typeof action.payload === 'object' && action.payload !== null) {
                state.loading = !!action.payload.loading;
                if (state.loading) state.lastFetchStatus = null;
                if (action.payload.practiceId !== undefined) {
                    state.loadingPracticeId = action.payload.practiceId ?? null;
                }
            } else {
                state.loading = !!action.payload;
            }
        },
        setError(state, action) {
            state.error = action.payload;
        },
        setFeatures(state, action) {
            state.features = action.payload;
        },
        setPracticeFeatures(state, action) {
            state.practiceFeatures = action.payload;
        },
        setRolePermissions(state, action) {
            state.rolePermissions = action.payload;
        },
        setUserOverrides(state, action) {
            state.userOverrides = action.payload;
        },
        clearPermissions(state) {
            state.permissions = {};
            state.roles = [];
            state.practiceId = null;
            state.loadingPracticeId = null;
            state.lastFetchStatus = null;
            // Reset loading/error too. On logout, invalidatePermissionsRequests()
            // supersedes any in-flight fetchMyPermissions, so its `finally` will
            // NOT flip loading back off (only the latest generation may). Without
            // resetting here, `loading` would stay stuck true (and any prior
            // `error` would persist) into the next login on the same tab (W3).
            state.loading = false;
            state.error = null;
        },
    },
});

export default slice.reducer;
export const { setPermissions, clearPermissions } = slice.actions;

// --------------- Thunks ---------------

// Best-effort HTTP status extraction from a thrown error (callApi normally
// returns {ok,status} without throwing, so this only fires on genuine
// exceptions). Mirrors the shape JWTContext.getFailureStatus reads.
function getErrorStatus(error) {
    return (
        error?.status
        || error?.response?.status
        || error?.payload?.status
        || 0
    );
}

// Monotonic guard for fetchMyPermissions (JAVASCRIPT-REACT-W3).
// fetchMyPermissions is dispatched from many places (auth init, login, all
// three navbars, the header practice switcher) so overlapping fetches during a
// practice switch are routine. Because a fetch can be delayed arbitrarily by
// the callApi token-refresh/retry path, responses can resolve OUT OF ORDER:
// a slow fetch for a previous practice can land after the current one and
// clobber the store with stale permissions (and rewind loadingPracticeId).
// Each call takes the next token; only the newest in-flight call is allowed to
// write to the store. Superseded responses are dropped.
let myPermissionsSeq = 0;

// Practice ids whose my-permissions fetch was STARTED in THIS page load and is
// either still in flight or resolved 200. Backs ensureMyPermissions() so a
// mount-effect re-request for a practice auth-init (or the header switcher)
// already covered is skipped instead of re-hitting the network.
//
// Deliberately module-level (page-load scoped) rather than derived from the
// Redux slice: the `permissions` slice is redux-persist'd (rootReducer
// whitelist), so slice.practiceId can be REHYDRATED from a previous session's
// localStorage. Keying the skip off persisted state could serve a previous
// user's permissions without any fetch. This Set starts empty on every page
// load, so the guard can only ever suppress a duplicate of a fetch this very
// page load performed.
//
// Cleared by invalidatePermissionsRequests(), which both logout paths and the
// auth-init session-clear path already call immediately before clearPermissions().
const myPermissionsCovered = new Set();

/**
 * Invalidate every in-flight fetchMyPermissions request.
 *
 * MUST be called on logout BEFORE clearPermissions: a permissions fetch that
 * was dispatched while still authenticated can resolve AFTER logout and, being
 * "latest", would write permissions back into a logged-out store. Bumping the
 * generation makes all outstanding calls superseded, so their responses are
 * dropped and cannot re-populate permissions post-logout (JAVASCRIPT-REACT-W3).
 *
 * Also drops the ensureMyPermissions coverage set, so the next account on this
 * tab can never skip a fetch on the strength of the previous account's result.
 */
export function invalidatePermissionsRequests() {
    myPermissionsSeq += 1;
    myPermissionsCovered.clear();
}

export function fetchMyPermissions(practiceId) {
    return async () => {
        myPermissionsSeq += 1;
        const seq = myPermissionsSeq;
        const isLatest = () => seq === myPermissionsSeq;
        const coverageKey = String(practiceId);
        // Marked covered synchronously (before the await) so a mount effect
        // firing in the SAME commit as an authoritative dispatch — e.g. the
        // header practice switcher and the navbar both reacting to a practiceId
        // change — is deduped rather than racing a second identical request.
        myPermissionsCovered.add(coverageKey);
        // Never leave a practice marked covered on a result we did not store.
        // Failing open (towards re-fetching) is the safe direction: the cost is
        // one extra request, whereas failing closed would leave the app on an
        // empty or stale permission map with nothing left to refresh it.
        const releaseCoverageUnlessStored = (stored) => {
            if (!stored) myPermissionsCovered.delete(coverageKey);
        };
        try {
            // Tag the loading flag with the target practiceId so that
            // feature-gate callers in flight can compare against it and skip
            // stale-practice fires during the practice-switch window.
            dispatch(slice.actions.setLoading({ loading: true, practiceId }));
            const response = await callApi(`/api/v1/permissions/my-permissions/?practice_id=${practiceId}`);
            // Superseded by a newer fetchMyPermissions (practice switch, or this
            // call was delayed by a token refresh). Drop the stale result rather
            // than overwrite the current practice's permissions.
            if (!isLatest()) {
                releaseCoverageUnlessStored(false);
                return { superseded: true, status: response?.status ?? 0 };
            }
            dispatch(slice.actions.setLastFetchStatus(response.status ?? 0));
            releaseCoverageUnlessStored(response.status === 200);
            if (response.status === 200) {
                // Stamp the snapshot with practiceId so hasFeatureAccess can
                // detect cross-practice reads.
                dispatch(slice.actions.setPermissions({ ...response.data, practiceId }));
            }
            // Surface the status so auth init (JWTContext) can detect a
            // refresh-rejection / auth-invalid permissions response (401/403)
            // and recover safely instead of staying "authenticated" with an
            // empty permission map.
            return { superseded: false, status: response.status };
        } catch (error) {
            releaseCoverageUnlessStored(false);
            if (!isLatest()) {
                return { superseded: true, status: getErrorStatus(error) };
            }
            dispatch(slice.actions.setLastFetchStatus(getErrorStatus(error)));
            dispatch(slice.actions.setError(error.message));
            return { superseded: false, status: getErrorStatus(error) };
        } finally {
            // Only the newest call may flip loading back off; a superseded call
            // must not rewind loadingPracticeId to its stale target.
            if (isLatest()) {
                dispatch(slice.actions.setLoading({ loading: false, practiceId }));
            }
        }
    };
}

/**
 * Mount-effect variant of fetchMyPermissions: fetch this practice's permissions
 * only if THIS page load has not already covered them.
 *
 * Why this exists. Three call sites are AUTHORITATIVE — auth init (JWTContext
 * bootstrap), login, and the header practice switcher — and each must always
 * hit the network, so they keep calling fetchMyPermissions directly. The three
 * navbars (vertical / mini / horizontal, exactly one mounted at a time) instead
 * fire a `[practiceId]` mount effect whose only job is to make sure permissions
 * exist for the current practice. On a cold dashboard load that effect ran
 * roughly 30ms after auth init had already resolved the very same request, so
 * every page load fetched /permissions/my-permissions/ twice and everything
 * gated on permissions waited for the second round trip.
 *
 * What is NOT deduped, by design:
 *   - login, and logging in as a different user on the same tab
 *     (invalidatePermissionsRequests() clears coverage on logout);
 *   - switching practice (the target practice is not covered yet, and the
 *     header dispatches the unconditional thunk regardless);
 *   - a hard reload (module state, and therefore coverage, starts empty);
 *   - any fetch that did not store a 200 (coverage is released), so a practice
 *     the user has no access to, or a failed/superseded request, is retried by
 *     the next mount exactly as it was before.
 */
export function ensureMyPermissions(practiceId) {
    return async (...args) => {
        if (practiceId == null) return { skipped: true, reason: 'no-practice' };
        if (myPermissionsCovered.has(String(practiceId))) {
            return { skipped: true, reason: 'already-covered' };
        }
        return fetchMyPermissions(practiceId)(...args);
    };
}

export function fetchFeatures() {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi('/api/v1/permissions/features/');
            if (response.status === 200) {
                dispatch(slice.actions.setFeatures(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function createFeature(data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi('/api/v1/permissions/features/', 'POST', data);
            if (response.status === 201) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function updateFeature(id, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/features/${id}/`, 'PATCH', data);
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchRolePermissions(featureId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/features/${featureId}/role-permissions/`);
            if (response.status === 200) {
                dispatch(slice.actions.setRolePermissions(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function updateRolePermissions(featureId, permissions, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/features/${featureId}/role-permissions/`, 'PUT', permissions);
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchPracticeFeatures(featureId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/practice-features/?feature__id=${featureId}`);
            if (response.status === 200) {
                dispatch(slice.actions.setPracticeFeatures(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function createPracticeFeature(data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi('/api/v1/permissions/practice-features/', 'POST', data);
            if (response.status === 201) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function togglePracticeFeature(id, isEnabled, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/practice-features/${id}/`, 'PATCH', { is_enabled: isEnabled });
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function bulkTogglePracticeFeatures(data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi('/api/v1/permissions/practice-features/bulk/', 'POST', data);
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchUserOverrides(featureId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/user-overrides/?feature__id=${featureId}`);
            if (response.status === 200) {
                dispatch(slice.actions.setUserOverrides(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function createUserOverride(data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi('/api/v1/permissions/user-overrides/', 'POST', data);
            if (response.status === 201) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function updateUserOverride(id, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/user-overrides/${id}/`, 'PATCH', data);
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function deleteUserOverride(id, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/user-overrides/${id}/`, 'DELETE');
            if (response.status === 204) {
                if (callback) callback();
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function addUserRole(practiceUserId, role, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/practice-users/${practiceUserId}/roles/`, 'POST', { role });
            if (response.status === 201) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function removeUserRole(practiceUserId, role, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callAdminApi(`/api/v1/permissions/practice-users/${practiceUserId}/roles/${role}/`, 'DELETE');
            if (response.status === 204) {
                if (callback) callback();
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

// ---------------------------------------------------------------------------
// Per-practice role access (feature x practice scope)
//
// These deliberately hand their result to the caller instead of writing it into
// the slice. The panel is scoped to one (featureId, practiceId) pair and the
// admin can switch pairs while a request is still in flight; a single shared
// slot in global state would let a slow response for practice A overwrite the
// screen the admin is now looking at for practice B. The component keys its own
// state by both ids and drops late responses, which is only possible if the
// data comes back to it rather than going somewhere global.
//
// They also do not toggle the shared `loading` flag, for the same reason: it is
// not per-scope, and the Feature Flags page uses it for other panels.
// ---------------------------------------------------------------------------

export function fetchPracticeRolePermissions(featureId, practiceId, { onSuccess, onError } = {}) {
    return async () => {
        const response = await callApi(
            `/api/v1/permissions/features/${featureId}/practices/${practiceId}/role-permissions/`,
        );
        if (response.status === 200) {
            if (onSuccess) onSuccess(response.data);
        } else if (onError) {
            onError(response);
        }
    };
}

export function savePracticeRolePermissions(
    featureId,
    practiceId,
    payload,
    { onSuccess, onConflict, onError } = {},
) {
    return async () => {
        const response = await callApi(
            `/api/v1/permissions/features/${featureId}/practices/${practiceId}/role-permissions/`,
            'PUT',
            payload,
        );
        if (response.status === 200) {
            if (onSuccess) onSuccess(response.data);
            return;
        }
        // 409 is not a generic failure: the rules moved under the admin, and the
        // response carries the current state to re-read. It gets its own path so
        // the panel can say what happened rather than showing "save failed".
        if (response.status === 409) {
            if (onConflict) onConflict(response.data);
            return;
        }
        if (onError) onError(response);
    };
}

export function previewPracticeAccess(featureId, practiceId, body, { onSuccess, onError } = {}) {
    return async () => {
        const response = await callApi(
            `/api/v1/permissions/features/${featureId}/practices/${practiceId}/access-preview/`,
            'POST',
            body,
        );
        if (response.status === 200) {
            if (onSuccess) onSuccess(response.data);
        } else if (onError) {
            onError(response);
        }
    };
}
